<template>
  <!-- Tool Detail Drawer (global) -->
  <ToolDetailDrawer
    v-model="toolDrawerVisible"
    :software-name="selectedSoftwareName" />

  <el-tabs
    v-model="activeTab"
    class="h-full overflow-hidden"
    @tab-change="handleTabChange">
    <!-- Dashboard Tab -->
    <el-tab-pane
      name="overview"
      :label="i18n.t('browserExtension.dashboard', 'Dashboard')"
      lazy>
      <DiscoveryDashboard @tool-click="handleToolClickFromDashboard" />
    </el-tab-pane>

    <!-- Tools Tab -->
    <el-tab-pane name="tools" :label="i18n.t('browserExtension.tools')" lazy>
      <DiscoveryToolsTab />
    </el-tab-pane>

    <!-- Persons Tab -->
    <el-tab-pane
      name="persons"
      :label="i18n.t('browserExtension.persons')"
      lazy>
      <DiscoveryPersonsTab />
    </el-tab-pane>

    <!-- Deployments Tab -->
    <el-tab-pane
      name="deployments"
      :label="i18n.t('browserExtension.deployments')"
      lazy>
      <DiscoveryDeploymentsTab />
    </el-tab-pane>
  </el-tabs>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import DiscoveryToolsTab from './tabs/DiscoveryToolsTab.vue'
  import DiscoveryDashboard from './dashboard/DiscoveryDashboard.vue'
  import DiscoveryPersonsTab from './tabs/DiscoveryPersonsTab.vue'
  import DiscoveryDeploymentsTab from './tabs/DiscoveryDeploymentsTab.vue'
  import ToolDetailDrawer from './drawers/ToolDetailDrawer.vue'
  import { useTabPersistence } from '@/composables/useTabPersistence'

  const i18n = useI18n()

  // Tool drawer state
  const toolDrawerVisible = ref(false)
  const selectedSoftwareName = ref<string | null>(null)

  // Handle tool click from dashboard - switch to tools tab and open drawer
  const handleToolClickFromDashboard = (toolName: string) => {
    selectedSoftwareName.value = toolName
    toolDrawerVisible.value = true
  }

  // Type-safe tab names
  type TabName = 'overview' | 'persons' | 'tools' | 'deployments'
  const VALID_TABS: readonly TabName[] = [
    'overview',
    'persons',
    'tools',
    'deployments',
  ]
  const STORAGE_KEY = 'browser-extension-tab'

  const { activeTab, handleTabChange } = useTabPersistence<TabName>(
    STORAGE_KEY,
    'overview',
    VALID_TABS
  )
</script>

<style lang="scss" scoped>
  .el-tabs {
    height: 100%;

    :deep(.el-tabs__content) {
      height: 100%;
      overflow: hidden;
    }

    :deep(.el-tab-pane) {
      height: 100%;
      overflow: auto;
    }
  }
</style>
